import { zonedTimeToUtc } from 'date-fns-tz';
import { differenceInHours } from 'date-fns';
import { t } from '../../utils/i18n';

export function formatPopulation(population: number) {
  const million = 1000000;
  if (population < million) {
    const populationText = population.toString();
    // Add spaces to the value e.g. "500 000"
    return populationText.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  const populationInDecimalFormat = (population / million).toFixed(1);
  return `${populationInDecimalFormat.replace('.', ',')} ${t('miljoonaa')}`;
}

const currencyByCode: Record<string, string> = {
  AED: t('Yhdistyneiden arabiemiirikuntien dirhami'),
  AFN: t('Afganistanin afgaani'),
  ALL: t('Albanian lek'),
  AMD: t('Armenian dram'),
  ANG: t('Alankomaiden Antillien guldeni'),
  AOA: t('Angolan kwanza'),
  ARS: t('Argentiinan peso'),
  AUD: t('Australian dollari'),
  AWG: t('Aruban floriini'),
  AZN: t('Azerbaidžanin manat'),
  BAM: t('Bosnian ja Hertsegovinan vaihdettava markka'),
  BBD: t('Barbadoksen dollari'),
  BDT: t('Bangladeshin taka'),
  BGN: t('Bulgarian lev'),
  BHD: t('Bahrainin dinaari'),
  BIF: t('Burundin frangi'),
  BMD: t('Bermudan dollari'),
  BND: t('Brunein dollari'),
  BOB: t('Bolivian boliviano'),
  BRL: t('Brasilian real'),
  BSD: t('Bahaman dollari'),
  BTN: t('Bhutanin ngultrum'),
  BWP: t('Botswanan pula'),
  BYN: t('Valko-Venäjän rupla'),
  BZD: t('Belizen dollari'),
  CAD: t('Kanadan dollari'),
  CDF: t('Kongon frangi'),
  CHF: t('Sveitsin frangi'),
  CLP: t('Chilen peso'),
  CNY: t('Kiinan juan'),
  COP: t('Kolumbian peso'),
  CRC: t('Costa Rican colón'),
  CUC: t('Kuuban vaihdettava peso'),
  CUP: t('Kuuban peso'),
  CVE: t('Kap Verden escudo'),
  CZK: t('Tšekin koruna'),
  DJF: t('Djiboutin frangi'),
  DKK: t('Tanskan kruunu'),
  DOP: t('Dominikaanisen tasavallan peso'),
  DZD: t('Algerian dinaari'),
  EGP: t('Egyptin punta'),
  ERN: t('Eritrean nakfa'),
  ETB: t('Etiopian birr'),
  EUR: t('Euro'),
  FJD: t('Fidžin dollari'),
  FKP: t('Falklandin punta'),
  GBP: t('Englannin punta'),
  GEL: t('Georgian lari'),
  GGP: t('Guernseyn punta'),
  GHS: t('Ghanan cedi'),
  GIP: t('Gibraltarin punta'),
  GMD: t('Gambian dalasi'),
  GNF: t('Guinean frangi'),
  GTQ: t('Guatemalan quetzal'),
  GYD: t('Guyanan dollari'),
  HKD: t('Hongkongin dollari'),
  HNL: t('Hondurasin lempira'),
  HRK: t('Kroatian kuna'),
  HTG: t('Haitin gourde'),
  HUF: t('Unkarin forintti'),
  IDR: t('Indonesian rupia'),
  ILS: t('Uusi Israelin sekeli'),
  IMP: t('Mansaaren punta'),
  INR: t('Intian rupia'),
  IQD: t('Irakin dinaari'),
  IRR: t('Iranin rial'),
  ISK: t('Islannin kruunu'),
  JEP: t('Jerseyn punta'),
  JMD: t('Jamaikan dollari'),
  JOD: t('Jordanian dinaari'),
  JPY: t('Jeni'),
  KES: t('Kenian šillinki'),
  KGS: t('Kirgisian som'),
  KHR: t('Kambodžan riel'),
  KMF: t('Komorien frangi'),
  KPW: t('Pohjois-Korean won'),
  KRW: t('Etelä-Korean won'),
  KWD: t('Kuwaitin dinaari'),
  KYD: t('Caymansaarten dollari'),
  KZT: t('Kazakstanin tenge'),
  LAK: t('Laosin kip'),
  LBP: t('Libanonin punta'),
  LKR: t('Sri Lankan rupia'),
  LRD: t('Liberian dollari'),
  LSL: t('Lesothon loti'),
  LYD: t('Libyan dinaari'),
  MAD: t('Marokon dirham'),
  MDL: t('Moldovan leu'),
  MGA: t('Madagaskarin ariary'),
  MKD: t('Makedonian denaari'),
  MMK: t('Myanmarin kyat'),
  MNT: t('Mongolian tögrög'),
  MOP: t('Macaon pataca'),
  MRO: t('Mauritanian ouguiya'),
  MUR: t('Mauritiuksen rupia'),
  MVR: t('Malediivien rufiyaa'),
  MWK: t('Malawin kwacha'),
  MXN: t('Meksikon peso'),
  MYR: t('Malesian ringgit'),
  MZN: t('Mosambikin metical'),
  NAD: t('Namibian dollari'),
  NGN: t('Nigerian naira'),
  NIO: t('Nicaraguan córdoba'),
  NOK: t('Norjan kruunu'),
  NPR: t('Nepalin rupia'),
  NZD: t('Uuden-Seelannin dollari'),
  OMR: t('Omanin rial'),
  PAB: t('Panaman balboa'),
  PEN: t('Perun sol'),
  PGK: t('Papua-Uuden-Guinean kina'),
  PHP: t('Filippiinien peso'),
  PKR: t('Pakistanin rupia'),
  PLN: t('Puolan złoty'),
  PRB: t('Transnistrian rupla'),
  PYG: t('Paraguayn guaraní'),
  QAR: t('Qatarin rial'),
  RON: t('Romanian leu'),
  RSD: t('Serbian dinaari'),
  RUB: t('Venäjän rupla'),
  RWF: t('Ruandan frangi'),
  SAR: t('Saudi-Arabian rial'),
  SBD: t('Salomonsaarten dollari'),
  SCR: t('Seychellien rupia'),
  SDG: t('Sudanin punta'),
  SEK: t('Ruotsin kruunu'),
  SGD: t('Singaporen dollari'),
  SHP: t('Saint Helenan punta'),
  SLL: t('Sierra Leonen leone'),
  SOS: t('Somalian šillinki'),
  SRD: t('Surinamen dollari'),
  SSP: t('Etelä-Sudanin punta'),
  STD: t('São Tomén ja Príncipen dobra'),
  SYP: t('Syyrian punta'),
  SZL: t('Swazimaan lilangeni'),
  THB: t('Thaimaan baht'),
  TJS: t('Tadžikistanin somoni'),
  TMT: t('Turkmenistanin manat'),
  TND: t('Tunisian dinaari'),
  TOP: t('Tongan paʻanga'),
  TRY: t('Turkin liira'),
  TTD: t('Trinidad ja Tobagon dollari'),
  TVD: t('Tuvalun dollari'),
  TWD: t('Uusi Taiwanin dollari'),
  TZS: t('Tansanian šillinki'),
  UAH: t('Ukrainan hryvnia'),
  UGX: t('Ugandan šillinki'),
  USD: t('Yhdysvaltain dollari'),
  UYU: t('Uruguayn peso'),
  UZS: t('Uzbekistanin som'),
  VEF: t('Venezuelan bolívar'),
  VND: t('Vietnamin đồng'),
  WST: t('Samoan tala'),
  XAF: t('CFA-frangi'),
  XCD: t('Itä-Karibian dollari'),
  XOF: t('CFA-frangi'),
  XPF: t('CFP-frangi'),
  YER: t('Jemenin rial'),
  ZAR: t('Etelä-Afrikan randi'),
  ZMW: t('Sambian kwacha')
};

export function formatCurrency(currencyCode: string) {
  return currencyByCode[currencyCode];
}

export function formatTimeDifference(timeZone: string) {
  try {
    const time = new Date();
    const comparisonTime = zonedTimeToUtc(time, 'Europe/Helsinki');
    const targetTime = zonedTimeToUtc(time, timeZone);
    const difference = differenceInHours(comparisonTime, targetTime);
    const sign = difference > 0 ? '+' : '';

    return `${sign}${difference} ${t('tuntia')}`;
  } catch (err) {
    return undefined;
  }
}
